import React from "react";

import { useParams } from "react-router-dom";

import type { ProjectMember } from "../../../../../hooks/useProjectSharing";
import {
  type ProjectViewLinkInvitation,
  type ProjectViewLinkInvitationID,
  useMutateUpdateProjectViewLinkInvitation,
} from "../../../../../hooks/useProjectViewLink";
import { useTracker } from "../../../../../hooks/useTracker";
import {
  useWebExport,
  WebExportAssignableUserRoles,
  WebExportAssignableUserRolesDisplayName,
} from "../../../../../hooks/useWebExport";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface InvitationRowProps {
  invitation: ProjectViewLinkInvitation;
  projectUser?: ProjectMember;
  onRemove?: (invitationId: ProjectViewLinkInvitationID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  tooltipText?: string;
}

export const InvitationRowViewLink = React.forwardRef<ShareRowRef, InvitationRowProps>(
  ({ invitation, onRemove, containerIntersectionObserver, tooltipText, projectUser }, ref) => {
    const tracker = useTracker();
    const { viewLink = "" } = useParams();
    const { data: webExportData } = useWebExport();

    const {
      mutate: mutateUpdateViewLinkInvitation,
      isPending: isUpdatePending,
      isError: isUpdateError,
    } = useMutateUpdateProjectViewLinkInvitation({
      invitationId: invitation.invitationId,
    });

    return (
      <ShareRow
        ref={ref}
        roles={WebExportAssignableUserRoles}
        roleDisplayName={WebExportAssignableUserRolesDisplayName}
        entity={invitation}
        containerIntersectionObserver={containerIntersectionObserver}
        onRemove={() => {
          onRemove?.(invitation.invitationId);
          tracker.trackShareModalMemberRemoveIntended({
            viewLinkId: viewLink,
            webExportId: webExportData?.webExport.webExportId,
            invitationId: invitation.invitationId,
            sharedResource: "review_link",
          });
        }}
        onUpdate={(prevRole, newRole) => {
          tracker.trackShareModalMemberRoleChanged({
            viewLinkId: viewLink,
            webExportId: webExportData?.webExport.webExportId,
            invitationId: invitation.invitationId,
            oldValue: prevRole,
            newValue: newRole,
            sharedResource: "review_link",
          });
          mutateUpdateViewLinkInvitation({
            role: newRole,
          });
        }}
        isPending={isUpdatePending}
        isError={isUpdateError}
        sharingBadges={[
          ...(projectUser?.isTeamUser === false ? ["Guest" as const] : []),
          "Invited",
        ]}
        tooltipText={tooltipText}
      />
    );
  }
);
